<template>
  <div v-if="dashboards.length" class="flex flex-col gap-4">
    <BaseSimpleSearchInput v-model="searchTerm" />
    <div
      v-if="getFilteredDashboard.length"
      class="flex flex-row flex-wrap items-center gap-2"
    >
      <BaseButton
        v-if="!isAllFilteredSelected"
        :title="$t('button.select_all')"
        size="extra-small"
        @click="selectAllFiltered"
      />
      <BaseButton
        v-if="selectedIds.length"
        :title="$t('button.unselect_all')"
        size="extra-small"
        @click="unselectAll"
      />
      <BaseButton
        v-if="selectedIds.length"
        :title="`${$t('button.subscribe')} (${selectedIds.length})`"
        icon="sign-in"
        color="primary"
        size="extra-small"
        :disabled="isSubscribing"
        class="dark:border-primary-500 dark:text-primary-300 dark:hover:bg-primary-900 dark:bg-slate-700"
        @click="subscribeSelected"
      />
    </div>
    <div
      v-for="dashboard in getFilteredDashboard"
      :key="dashboard.id"
      class="squircle-md flex flex-col gap-2 border p-2"
    >
      <div class="flex flex-row">
        <h3 class="flex flex-1 items-center gap-2 text-base font-bold">
          <button
            type="button"
            class="flex flex-1 items-center gap-2 text-left"
            @click="() => toggleSelection(dashboard.id)"
          >
            <FontAwesomeIcon
              :icon="[
                'far',
                isSelected(dashboard.id) ? 'circle-check' : 'circle'
              ]"
            />
            <FontAwesomeIcon :icon="['fal', getIcon(dashboard)]" />
            {{ $trans(dashboard.name) }}
          </button>
        </h3>
        <span class="flex flex-shrink-0 gap-x-1">
          <BaseButton
            :title="$t('button.subscribe')"
            icon="sign-in"
            size="extra-small"
            :disabled="isSubscribing"
            class="dark:border-primary-500 dark:text-primary-300 dark:hover:bg-primary-900 dark:bg-slate-700"
            @click="() => subscribe(dashboard.id)"
          />
          <BaseButton
            v-if="canDelete(dashboard)"
            :title="$t('button.delete')"
            icon="trash"
            size="extra-small"
            color="danger"
            @click="() => deleteDashboard(dashboard)"
          />
        </span>
      </div>
      <div class="flex flex-row">
        <div class="flex-shrink-0">
          <AvatarComponent
            :user_id="dashboard.creator_id"
            :badge="true"
            size="6"
          />
        </div>
        <div class="flex flex-1 flex-row justify-end">
          <AvatarComponent
            v-for="user_id in dashboard.subscriber_ids"
            :key="user_id"
            :user_id="user_id"
            :badge="false"
            size="6"
          />
        </div>
      </div>
    </div>
  </div>
  <ConfirmModal ref="deleteTemplateDashboard" />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseSimpleSearchInput from '@c/addf-package/components/BaseInput/BaseSimpleSearchInput.vue'
import AvatarComponent from '@c/AvatarComponent.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import DashboardTypes from '@/assets/enums/dashboard/DashboardTypes'
import {
  failedIds,
  isAllSelected,
  keepAvailable,
  selectAll,
  subscribableIds,
  toggleSelection
} from '@c/Dashboard/Components/DashboardComponent/dashboardSelection'

export default {
  name: 'DashboardPublic',
  components: {
    AvatarComponent,
    BaseButton,
    BaseSimpleSearchInput,
    ConfirmModal,
    FontAwesomeIcon
  },
  props: {
    dashboards: {
      type: Array,
      required: true
    },
    typeDashboardAvailable: {
      type: Array,
      required: false,
      default: () => Object.values(DashboardTypes)
    }
  },
  emits: ['subscribed', 'deleted'],
  data() {
    return {
      searchTerm: '',
      selectedIds: [],
      isSubscribing: false
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      dashboardList: 'metadata/getTemplateDashboards'
    }),
    getSubscribedIds() {
      return this.dashboardList.map((item) => item.id)
    },
    getUnsubscribedDashboard() {
      return this.dashboards
        .filter((item) => this.typeDashboardAvailable.includes(item.type))
        .filter((item) => !this.getSubscribedIds.includes(item.id))
    },
    getFilteredDashboard() {
      const term = this.searchTerm.trim().toLowerCase()
      if (!term) return this.getUnsubscribedDashboard

      return this.getUnsubscribedDashboard.filter((item) =>
        this.$trans(item.name)?.toLowerCase().includes(term)
      )
    },
    selectableIds() {
      return this.getFilteredDashboard.map((item) => item.id)
    },
    isAllFilteredSelected() {
      return isAllSelected(this.selectedIds, this.selectableIds)
    },
    isSuperAdmin() {
      return this.auth?.roles?.includes('super-admin') ?? false
    }
  },
  watch: {
    // Une vue abonnée (ou supprimée) sort de la liste des non-suivies :
    // on purge la sélection pour ne jamais reposter un abonnement déjà fait.
    getUnsubscribedDashboard(dashboards) {
      this.selectedIds = keepAvailable(
        this.selectedIds,
        dashboards.map((item) => item.id)
      )
    }
  },
  methods: {
    ...mapActions({
      processSubscribeToTemplateDashboardAction:
        'dashboard/subscribeToTemplateDashboard',
      processDeleteTemplateDashboardByIdAction:
        'dashboard/deleteTemplateDashboardById',
      processUndoDeleteTemplateDashboardByIdAction:
        'dashboard/undoDeleteTemplateDashboardById',
      processRefreshSpecificInitialMetadataModelsAction:
        'metadata/refreshSpecificInitialMetadataModels'
    }),
    analyticsGroupIcon(dashboard) {
      const iconByGroup = {
        dossier: 'book',
        invoicing: 'receipt',
        event: 'calendar',
        invoice_item: 'sitemap'
      }
      return iconByGroup[dashboard.group] ?? 'chart-bar'
    },
    getIcon(dashboard) {
      switch (dashboard.type) {
        case DashboardTypes.AGENDA:
          return 'calendar-day'
        case DashboardTypes.ANALYTICS:
          return this.analyticsGroupIcon(dashboard)
        case DashboardTypes.STATISTICS:
          return 'chart-bar'
        case DashboardTypes.INVOICE:
        case DashboardTypes.PURCHASE_ORDER:
        case DashboardTypes.QUOTE:
        case DashboardTypes.CREDIT_NOTE:
          return 'euro-sign'
        case DashboardTypes.DASHBOARD:
          return 'table'
        case DashboardTypes.SUPPLIER_ORDER:
          return 'truck'
        default:
          return 'house'
      }
    },
    canDelete(dashboard) {
      return (
        !!dashboard?.id &&
        (this.isSuperAdmin ||
          this.$can('manage_dashboards') ||
          dashboard.creator_id === this.auth?.id)
      )
    },
    async deleteDashboard(dashboard) {
      if (!this.canDelete(dashboard)) return

      const subscriber_ids = dashboard?.subscriber_ids
      // Avertissement explicite dès qu'une autre personne reste abonnée.
      // Liste absente = on avertit quand même plutôt que supprimer en silence.
      const hasOtherSubscribers =
        !Array.isArray(subscriber_ids) ||
        subscriber_ids.some((user_id) => user_id !== this.auth?.id)

      const confirmDeletion = await this.$refs.deleteTemplateDashboard.show({
        title: hasOtherSubscribers
          ? this.$t('toasts.warning_delete_template_dashboard')
          : this.$t('validation.confirm_delete'),
        okButton: this.$t('button.delete')
      })

      if (!confirmDeletion) return

      const id = dashboard.id
      this.processDeleteTemplateDashboardByIdAction({ id }).then(() => {
        this.processRefreshSpecificInitialMetadataModelsAction({
          model: 'template_dashboards'
        }).then(() => {
          this.$h.toastAction(
            'delete',
            this.$t('toasts.deleted_m', { var: this.$t('global.dashboard') }),
            null,
            [
              {
                title: this.$t('button.cancel'),
                class: 'text-red-700',
                action: () => this.undoDeleteDashboard(id)
              }
            ]
          )
          this.$emit('deleted', id)
        })
      })
    },
    undoDeleteDashboard(id) {
      this.processUndoDeleteTemplateDashboardByIdAction({ id }).then(() => {
        this.processRefreshSpecificInitialMetadataModelsAction({
          model: 'template_dashboards'
        }).then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.reseted_m', { var: this.$t('global.dashboard') })
          )
        })
      })
    },
    isSelected(id) {
      return this.selectedIds.includes(id)
    },
    toggleSelection(id) {
      this.selectedIds = toggleSelection(this.selectedIds, id)
    },
    selectAllFiltered() {
      this.selectedIds = selectAll(this.selectedIds, this.selectableIds)
    },
    unselectAll() {
      this.selectedIds = []
    },
    async subscribeSelected() {
      if (this.isSubscribing) return

      // Garde anti-doublon : on ne poste que les vues encore non suivies.
      const ids = subscribableIds(this.selectedIds, this.getSubscribedIds)

      if (!ids.length) {
        this.selectedIds = []
        return
      }

      this.isSubscribing = true

      try {
        const results = await Promise.allSettled(
          ids.map((template_dashboard_id) =>
            this.processSubscribeToTemplateDashboardAction({
              template_dashboard_id
            })
          )
        )

        const failed = failedIds(ids, results)
        const subscribed = results
          .filter((result) => result.status === 'fulfilled')
          .map((result) => result.value?.template_dashboard)
          .filter(Boolean)

        if (!failed.length && subscribed.length) {
          this.$emit('subscribed', subscribed[0])
          return
        }

        // Échec partiel : le parent fermerait la modale, on reste donc ici.
        // Les vues en échec restent cochées pour pouvoir réessayer ; le
        // rafraîchissement retire les vues abonnées de la liste.
        this.selectedIds = failed
        if (failed.length < ids.length) {
          await this.processRefreshSpecificInitialMetadataModelsAction({
            model: 'template_dashboards'
          })
        }
      } finally {
        this.isSubscribing = false
      }
    },
    subscribe(template_dashboard_id) {
      this.processSubscribeToTemplateDashboardAction({
        template_dashboard_id
      }).then((response) =>
        this.$emit('subscribed', response.template_dashboard)
      )
    }
  }
}
</script>

<style scoped></style>
